import { v4 as uuid } from 'uuid';

import { convertTextWithCitations } from './sources.utils';

import { ChatStatusHelpers } from '../constants';

import type { ChatStatus, ChatUIMessage, CitationSource } from '../types';
import type { Message as AGUIMessage } from '@ag-ui/core';
import type { UIMessage } from 'ai';

export const createMessage = <T extends UIMessage = UIMessage>(
  text: string,
  role: 'user' | 'assistant' | 'system',
): T => {
  return {
    id: uuid(),
    role,
    parts: [
      {
        type: 'text',
        text: text.trim(),
        state: 'done',
      },
    ],
    metadata: {
      pending: true, // Mark as unsent
    },
  } as T;
};

export const createUserMessage = (text: string): ChatUIMessage => {
  return createMessage<ChatUIMessage>(text, 'user');
};

type ApiThreadMessage = {
  message: AGUIMessage;
  citation_sources?: CitationSource[];
};

export const convertApiMessagesToUIMessages = (
  messages: ApiThreadMessage[],
): ChatUIMessage[] => {
  return messages.map(({ message, citation_sources }) => {
    const role = message.role as 'user' | 'assistant' | 'system';
    const text = convertTextWithCitations(
      typeof message.content === 'string' ? message.content : '',
    );

    const parts: ChatUIMessage['parts'] = [
      {
        type: 'text',
        text,
        state: 'done',
      },
    ];

    if (citation_sources?.length) {
      // Pass the indexed sparse array through verbatim — the renderer's
      // `[^N]` markers look up `sources[N - 1]`, so collapsing undefined
      // slots (e.g. via dedupeSourcesByUrl) would shift every later
      // citation up by one and leave the last marker orphaned.
      parts.push({
        type: 'data-citation_sources',
        data: citation_sources,
      });
    }

    return {
      id: message.id,
      role,
      parts,
    };
  });
};

/**
 * Determines if an incomplete message should be filtered out from the chat UI.
 * Returns true when the message is the current (last) message, streaming has completed,
 * and the message contains no meaningful content (text, citations, or guided flow parts).
 */
export const shouldFilterIncompleteMessage = (
  message: UIMessage,
  status?: ChatStatus,
  isCurrentMessage?: boolean,
  extraPartTypes?: string[],
): boolean => {
  if (!isCurrentMessage || !status || ChatStatusHelpers.isProcessing(status)) {
    return false;
  }

  const hasTextContent = message.parts?.some(
    (part) => part.type === 'text' && part.text?.trim(),
  );

  const hasSourcesOrOtherContent = message.parts?.some(
    (part) =>
      part.type === 'data-citation_sources' &&
      Array.isArray(part.data) &&
      part.data.length > 0,
  );

  const hasExtraContent =
    extraPartTypes &&
    extraPartTypes.length > 0 &&
    message.parts?.some((part) => extraPartTypes.includes(part.type as string));

  return !hasTextContent && !hasSourcesOrOtherContent && !hasExtraContent;
};

export const findPrecedingUserQuestion = (
  messages: UIMessage[],
  currentIndex: number,
): string | undefined => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      return messages[i].parts?.find((p) => p.type === 'text')?.text;
    }
  }
  return undefined;
};

export const findPrecedingUserMessageId = (
  messages: UIMessage[],
  currentIndex: number,
): string | undefined => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      return messages[i].id;
    }
  }
  return undefined;
};
